import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import {
  FileText,
  Calendar,
  DollarSign,
  Building2,
  Upload,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Clock,
  X,
  Loader2,
  Trash2,
  Download,
  ExternalLink
} from 'lucide-react';
import { WorkflowCard } from '../shared/WorkflowCard';
import { WorkflowContext } from '../../../../types/workflow.types';
import { contractService } from '../../../../services/api/contractService';
import { useAuth } from '../../../../contexts/AuthContext';
import { decodeHtmlEntities } from '../../../../utils/textUtils';
import { downloadContractFile } from '../../../../utils/downloadContractFile';
import ContractDetailsModal from '../../contracts/ContractDetailsModal';

interface OutletContext extends WorkflowContext {
  onNextStep: () => void;
  onPrevStep: () => void;
  refreshContract: () => Promise<void>;
  refreshAnalysis: () => Promise<void>;
}

interface ContractFile {
  _id?: string;
  id?: string;
  filename?: string;
  originalName?: string;
  mimeType?: string;
  size?: number;
  uploadedAt?: string;
}

// Allowed file extensions
const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.json', '.txt', '.xls', '.xlsx'];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export const Step1_ContractOverview: React.FC = () => {
  const context = useOutletContext<OutletContext>();
  const { contract, analysis, isLoading, onNextStep, refreshContract } = context;
  const { token } = useAuth();
  const navigate = useNavigate();
  const { id: contractId } = useParams<{ id: string }>();

  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<ContractFile[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [isDeletingFile, setIsDeletingFile] = useState<string | null>(null);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch uploaded files when contract loads
  useEffect(() => {
    const fetchFiles = async () => {
      if (!contract || !token) return;

      const contractId = contract._id || contract.id;
      if (!contractId) return;

      setIsLoadingFiles(true);
      try {
        const files = await contractService.getFiles(token, contractId);
        setUploadedFiles(files || []);
      } catch (error) {
        console.error('Error fetching files:', error);
        // Don't show error to user, just log it - files might not exist yet
      } finally {
        setIsLoadingFiles(false);
      }
    };

    fetchFiles();
  }, [contract, token]);

  // Validate file
  const validateFile = useCallback((file: File): string | null => {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return `File "${file.name}" is too large. Maximum size is 10MB.`;
    }

    // Check file type
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      return `File type "${extension}" is not supported. Allowed types: ${ALLOWED_EXTENSIONS.join(', ')}`;
    }

    return null;
  }, []);

  // Handle file upload
  const handleFileUpload = useCallback(async (files: FileList | File[]) => {
    if (!contract || !token) return;

    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;

    // Validate all files first
    for (const file of fileArray) {
      const error = validateFile(file);
      if (error) {
        setUploadError(error);
        return;
      }
    }

    setIsUploading(true);
    setUploadError(null);
    setUploadSuccess(null);
    setUploadProgress(0);

    try {
      const contractId = contract._id || contract.id;
      let uploadedCount = 0;

      for (const file of fileArray) {
        await contractService.uploadFile(token, contractId, file, {
          documentType: 'contract',
          uploadedAt: new Date().toISOString()
        });
        uploadedCount++;
        setUploadProgress(Math.round((uploadedCount / fileArray.length) * 100));
      }

      setUploadSuccess(`Successfully uploaded ${uploadedCount} file${uploadedCount > 1 ? 's' : ''}`);

      // Refresh the files list
      const files = await contractService.getFiles(token, contractId);
      setUploadedFiles(files || []);

      // Also refresh contract data
      if (refreshContract) {
        await refreshContract();
      }

      // Clear success message after 3 seconds
      setTimeout(() => setUploadSuccess(null), 3000);
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error instanceof Error ? error.message : 'Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  }, [contract, token, validateFile, refreshContract]);

  // Handle drag events
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files);
    }
  }, [handleFileUpload]);

  // Handle file input change
  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileUpload(files);
    }
    // Reset input to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [handleFileUpload]);

  // Open file browser
  const openFileBrowser = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Handle file deletion
  const handleDownloadFile = useCallback(async (fileId: string, filename: string) => {
    if (!token || !fileId) return;
    setDownloadingFileId(fileId);
    try {
      await downloadContractFile(token, fileId, filename);
    } catch (error) {
      console.error('File download failed:', error);
      // The card's error banner is generic, so it carries download failures too.
      setUploadError(error instanceof Error && error.message ? error.message : `Couldn't download ${filename}. Try again.`);
    } finally {
      setDownloadingFileId(null);
    }
  }, [token]);

  const handleDeleteFile = useCallback(async (fileId: string) => {
    if (!contract || !token) return;

    const confirmed = window.confirm('Are you sure you want to delete this file?');
    if (!confirmed) return;

    setIsDeletingFile(fileId);
    try {
      const contractId = contract._id || contract.id;
      await contractService.deleteFile(token, contractId, fileId);

      // Remove file from local state
      setUploadedFiles(prev => prev.filter(f => (f._id || f.id) !== fileId));
      setUploadSuccess('File deleted successfully');
      setTimeout(() => setUploadSuccess(null), 3000);
    } catch (error) {
      console.error('Delete error:', error);
      setUploadError('Failed to delete file');
    } finally {
      setIsDeletingFile(null);
    }
  }, [contract, token]);

  // Handle navigation - always use direct navigation for reliability
  const handleProceedToAnalysis = useCallback(() => {
    console.log('Proceed to Analysis clicked, contractId:', contractId);

    // Always use direct navigation to analysis step
    if (contractId) {
      navigate(`/contracts/${contractId}/analysis`);
    } else {
      console.error('contractId not available');
    }
  }, [contractId, navigate]);

  if (isLoading || !contract) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
        <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-xl" />
      </div>
    );
  }

  const formatCurrency = (value: number, currency?: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'draft':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'expired':
      case 'terminated':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <FileText className="w-5 h-5 text-gray-500" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Contract Overview
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Review the contract details and upload documents for analysis
        </p>
      </div>

      {/* Contract Details Card */}
      <WorkflowCard
        title="Contract Details"
        icon={<FileText className="w-5 h-5" />}
        actions={
          <button
            onClick={() => setIsDetailsModalOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            View Full Details
          </button>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Contract Title
              </label>
              <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                {decodeHtmlEntities(contract.contractTitle)}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Supplier
              </label>
              <div className="flex items-center gap-2 mt-1">
                <Building2 className="w-5 h-5 text-gray-400" />
                <p className="text-gray-900 dark:text-white">{contract.supplier}</p>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Status
              </label>
              <div className="flex items-center gap-2 mt-1">
                {getStatusIcon(contract.status)}
                <span className="capitalize text-gray-900 dark:text-white">
                  {contract.status}
                </span>
              </div>
            </div>

            {contract.category && (
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Category
                </label>
                <p className="text-gray-900 dark:text-white mt-1">{contract.category}</p>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Contract Period
              </label>
              <div className="flex items-center gap-2 mt-1">
                <Calendar className="w-5 h-5 text-gray-400" />
                <span className="text-gray-900 dark:text-white">
                  {formatDate(contract.startDate)} - {formatDate(contract.endDate)}
                </span>
              </div>
            </div>

            {contract.value !== undefined && contract.value > 0 && (
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Contract Value
                </label>
                <div className="flex items-center gap-2 mt-1">
                  <DollarSign className="w-5 h-5 text-gray-400" />
                  <span className="text-xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(contract.value, contract.paymentCurrency)}
                  </span>
                </div>
              </div>
            )}

            {contract.paymentTerms && (
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Payment Terms
                </label>
                <p className="text-gray-900 dark:text-white mt-1">
                  {contract.paymentTerms}
                </p>
              </div>
            )}

            {contract.department && (
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Department
                </label>
                <p className="text-gray-900 dark:text-white mt-1">
                  {contract.department}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        {contract.description && (
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-dark-border">
            <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Description
            </label>
            <p className="text-gray-700 dark:text-gray-300 mt-2">
              {contract.description}
            </p>
          </div>
        )}
      </WorkflowCard>

      {/* Documents Section */}
      <WorkflowCard
        title="Contract Documents"
        icon={<Upload className="w-5 h-5" />}
      >
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ALLOWED_EXTENSIONS.join(',')}
          onChange={handleFileInputChange}
          className="hidden"
        />

        {/* Error Message */}
        {uploadError && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-700 dark:text-red-300">{uploadError}</p>
            </div>
            <button
              onClick={() => setUploadError(null)}
              className="text-red-500 hover:text-red-700 dark:hover:text-red-300"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Success Message */}
        {uploadSuccess && (
          <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <p className="text-sm text-green-700 dark:text-green-300">{uploadSuccess}</p>
          </div>
        )}

        {/* Drag and Drop Zone */}
        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={openFileBrowser}
          className={`
            relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer
            transition-all duration-200
            ${isDragging
              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
              : 'border-gray-300 dark:border-gray-600 hover:border-primary-400 hover:bg-gray-50 dark:hover:bg-gray-800/50'
            }
            ${isUploading ? 'pointer-events-none opacity-75' : ''}
          `}
        >
          {isUploading ? (
            <div className="space-y-3">
              <Loader2 className="w-12 h-12 text-primary-500 mx-auto animate-spin" />
              <p className="text-gray-700 dark:text-gray-300 font-medium">
                Uploading... {uploadProgress}%
              </p>
              <div className="w-48 mx-auto h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-500 transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          ) : (
            <>
              <div className={`
                w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center
                ${isDragging
                  ? 'bg-primary-100 dark:bg-primary-900/30'
                  : 'bg-gray-100 dark:bg-gray-800'
                }
              `}>
                <Upload className={`w-8 h-8 ${isDragging ? 'text-primary-500' : 'text-gray-400'}`} />
              </div>
              <p className="text-gray-700 dark:text-gray-300 font-medium mb-1">
                {isDragging ? 'Drop files here' : 'Drag and drop files here'}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                or click to browse
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500">
                Supported: PDF, Word, Excel, JSON, TXT (max 10MB)
              </p>
            </>
          )}
        </div>

        {/* Uploaded Files List */}
        {isLoadingFiles ? (
          <div className="mt-6 flex items-center justify-center py-4">
            <Loader2 className="w-6 h-6 text-primary-500 animate-spin" />
            <span className="ml-2 text-gray-500 dark:text-gray-400">Loading files...</span>
          </div>
        ) : uploadedFiles.length > 0 ? (
          <div className="mt-6 space-y-3">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Uploaded Documents ({uploadedFiles.length})
            </h4>
            {uploadedFiles.map((file, index) => (
              <div
                key={file._id || file.id || index}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg group"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                    <FileText className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {file.originalName || file.filename || 'Unknown file'}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {file.size ? `${(file.size / 1024).toFixed(1)} KB` : 'Unknown size'}
                      {file.uploadedAt && ` - Uploaded ${new Date(file.uploadedAt).toLocaleDateString()}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleDownloadFile(file._id || file.id || '', file.originalName || file.filename || 'contract')}
                    disabled={downloadingFileId === (file._id || file.id)}
                    className="p-2 text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors disabled:opacity-50"
                    title="Download"
                    aria-label={`Download ${file.originalName || file.filename || 'file'}`}
                  >
                    {downloadingFileId === (file._id || file.id) ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={() => handleDeleteFile(file._id || file.id || '')}
                    disabled={isDeletingFile === (file._id || file.id)}
                    className="p-2 text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                    title="Delete"
                  >
                    {isDeletingFile === (file._id || file.id) ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-6 text-center py-4 text-gray-500 dark:text-gray-400 text-sm">
            No documents uploaded yet. Upload a contract document to proceed with analysis.
          </div>
        )}
      </WorkflowCard>

      {/* Analysis Status */}
      {analysis ? (
        <WorkflowCard
          title="Analysis Status"
          icon={<CheckCircle className="w-5 h-5 text-green-500" />}
        >
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <p className="text-green-600 dark:text-green-400 font-medium">
                AI Analysis Complete
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {analysis.clauses?.length || 0} clauses analyzed
              </p>
            </div>
            <button
              onClick={handleProceedToAnalysis}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            >
              View Analysis
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </WorkflowCard>
      ) : (
        <WorkflowCard
          title="Ready for Analysis"
          icon={<AlertCircle className="w-5 h-5 text-yellow-500" />}
        >
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <p className="text-gray-700 dark:text-gray-300">
                This contract has not been analyzed yet. Proceed to run AI analysis.
              </p>
            </div>
            <button
              onClick={handleProceedToAnalysis}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
            >
              Run Analysis
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </WorkflowCard>
      )}

      {/* Contract Details Modal */}
      <ContractDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        contract={contract ? {
          ...contract,
          title: contract.contractTitle,
          contractNumber: contract.id,
          currency: (contract as any).currency || 'USD',
          createdAt: (contract as any).createdAt || new Date().toISOString(),
          updatedAt: (contract as any).updatedAt || new Date().toISOString(),
          negotiationEmail: contract.negotiationEmail
        } as any : null}
      />
    </div>
  );
};
